<script setup>
import { Link, usePage } from '@inertiajs/vue3'
import { XMarkIcon } from '@heroicons/vue/20/solid'
import { computed, onMounted, ref } from 'vue'

// Single promo bar for the whole site. Nova's header-banner enum (finder) turns
// on exactly one of these flags, so at most one variant ever renders here.
const page = usePage()

const variant = computed(() => {
  const features = page.props.features || {}
  if (features.app_promo_banner) return 'app'
  if (features.subscription_promo_banner) return 'subscription'
  return null
})

const appStoreUrl = computed(() => page.props.appDownload?.appStore || '')
const playStoreUrl = computed(() => page.props.appDownload?.playStore || '')

// Per-variant dismissal so dismissing one banner never hides the other. Bump the
// suffix when a message changes to re-show it to people who dismissed the old one.
const storageKey = computed(() =>
  variant.value === 'app' ? 'promo_banner_app_dismissed_v1' : 'promo_banner_dismissed_v2',
)

// Visible by default; corrected on the client since localStorage can't be read
// during SSR, so a new visitor sees it immediately.
const visible = ref(true)

onMounted(() => {
  try {
    if (window.localStorage.getItem(storageKey.value) === '1') visible.value = false
  } catch {
    // Private mode / storage blocked, just leave it visible.
  }
})

const dismiss = () => {
  visible.value = false
  try {
    window.localStorage.setItem(storageKey.value, '1')
  } catch {
    // Ignore, dismissal simply won't persist across reloads.
  }
}
</script>

<template>
  <div v-if="variant && visible" dusk="promo-banner" class="relative bg-indigo-600 text-white">
    <div class="mx-auto flex max-w-7xl items-center justify-center gap-x-4 px-12 py-2.5 sm:px-6">
      <p v-if="variant === 'subscription'" class="text-sm leading-6">
        Introducing <span class="font-semibold">📚 ABN Books</span>: a dedicated tax agent and everything
        your business needs, from <span class="font-semibold">A$73/month</span>.
        <Link href="/membership" class="font-semibold underline underline-offset-2 hover:text-indigo-100">
          See the plans
        </Link>
      </p>
      <div v-else class="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-sm leading-6">
        <span>
          <span class="mr-1 align-middle text-xl leading-none">📱</span>
          <span class="font-semibold">The SoleTrader app is here.</span>
          Manage your ABN services from your phone.
        </span>
        <span class="flex items-center gap-2">
          <a
            v-if="appStoreUrl"
            :href="appStoreUrl"
            target="_blank"
            rel="noopener"
            dusk="promo-app-store-link"
            aria-label="Download on the App Store"
            class="inline-flex items-center gap-1.5 rounded-lg border border-white/15 bg-white/5 px-2.5 py-1 font-semibold leading-none transition hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            <svg class="size-4 flex-none" viewBox="0 0 384 512" fill="currentColor" aria-hidden="true">
              <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.5 43-17.8 75.8-17.8 31.8 0 48.3 17.8 76.4 17.8 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
            </svg>
            App Store
          </a>
          <a
            v-if="playStoreUrl"
            :href="playStoreUrl"
            target="_blank"
            rel="noopener"
            dusk="promo-play-store-link"
            aria-label="Get it on Google Play"
            class="inline-flex items-center gap-1.5 rounded-lg border border-white/15 bg-white/5 px-2.5 py-1 font-semibold leading-none transition hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            <svg class="size-3.5 flex-none" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#00D2FF" d="M3.6 2.2 13.3 12 3.6 21.8a1.7 1.7 0 0 1-.6-1.3V3.5c0-.5.2-1 .6-1.3Z" />
              <path fill="#00E676" d="M3.6 2.2c.4-.2.9-.2 1.4.1l11.7 6.6-2.9 2.9L3.6 2.2Z" />
              <path fill="#FFCE00" d="M16.7 8.9 20 10.8c.9.5.9 1.9 0 2.4l-3.3 1.9-3-3 3-3.2Z" />
              <path fill="#FF3D47" d="m3.6 21.8 10.2-9.9 2.9 2.9L5 21.7c-.5.3-1 .3-1.4.1Z" />
            </svg>
            Google Play
          </a>
        </span>
      </div>
    </div>
    <button
      type="button"
      dusk="promo-banner-dismiss"
      @click="dismiss"
      aria-label="Dismiss"
      class="absolute inset-y-0 right-1 flex items-center px-3 text-indigo-100 transition-colors hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
    >
      <XMarkIcon class="size-5" aria-hidden="true" />
    </button>
  </div>
</template>
